import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, ArchiveRestore, CalendarClock, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiException, api } from '../../api/client'
import { endOfDayInStudioZone, toStudioDate } from '../../lib/deliveryDate'
import { formatBytes } from '../../lib/format'
import { Banner, Button, Dialog, Field, Menu, MenuItem } from '../../ui'

interface Props {
  projectId: string
  name: string
  status: string
  /** Para poder decir cuánto se libera antes de confirmar, no después. */
  storageBytes: number
  /** ISO o null. Cuando llega, los enlaces dejan de abrirse. */
  expiresAt: string | null
}

type Open = 'rename' | 'archive' | 'delete' | 'expiry' | null

/**
 * Renombrar, archivar y borrar un proyecto.
 *
 * `PATCH` y `DELETE /projects/{id}` estaban desde F0 sin nada que los llamara.
 * La lista ya pintaba la insignia de estado —«Activo», «Archivado»,
 * «Borrador»— y nada podía cambiarla: todo proyecto se quedaba para siempre en
 * el estado con el que nació.
 *
 * Lo que se dice en cada confirmación sale de mirar qué hace el código, no de
 * lo que suena razonable:
 *
 *   - Archivar es SÓLO una etiqueta. Nada en el backend lee `projects.status`,
 *     así que el enlace del cliente sigue funcionando igual. Prometer lo
 *     contrario haría que un estudio archivara para «cerrar» una entrega.
 *   - Borrar SÍ rompe el enlace: `BrowseSharedProjectService` filtra por
 *     `deleted_at IS NULL`, y la galería del cliente deja de abrir en el acto.
 *   - Borrar va a la PAPELERA: libera la cuota al momento y deja los archivos
 *     siete días por si fue un error. Eso hay que decirlo, o el estudio no
 *     sabe que tiene una salida.
 */
export function ProjectActions({ projectId, name, status, storageBytes, expiresAt }: Props) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState<Open>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const archived = status === 'archived'

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  const setStatus = useMutation({
    mutationFn: (value: 'active' | 'archived') =>
      api.patch<{ data: unknown }>(`/projects/${projectId}`, { status: value }),
    onSuccess: () => {
      setFailure(null)
      setOpen(null)
      refresh()
    },
    onError: (error) => {
      setOpen(null)
      setFailure(error instanceof ApiException ? error.message : 'No se pudo cambiar el estado.')
    },
  })

  const remove = useMutation({
    mutationFn: () => api.delete<{ data: { restorable_days: number } }>(`/projects/${projectId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['projects', 'trash'] })
      void queryClient.invalidateQueries({ queryKey: ['tenant-usage'] })
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
      // A la lista, que es donde está la papelera: quien acaba de borrar por
      // error tiene el botón de recuperar delante sin buscarlo.
      void navigate('/projects', { replace: true })
    },
    onError: (error) => {
      setOpen(null)
      setFailure(error instanceof ApiException ? error.message : 'No se pudo borrar el proyecto.')
    },
  })

  return (
    <>
      <Menu label="Acciones del proyecto">
        <MenuItem onClick={() => setOpen('rename')}>
          <Pencil size={16} strokeWidth={1.75} aria-hidden />
          Renombrar
        </MenuItem>

        {archived ? (
          <MenuItem onClick={() => setStatus.mutate('active')}>
            <ArchiveRestore size={16} strokeWidth={1.75} aria-hidden />
            Sacar del archivo
          </MenuItem>
        ) : (
          <MenuItem onClick={() => setOpen('archive')}>
            <Archive size={16} strokeWidth={1.75} aria-hidden />
            Archivar
          </MenuItem>
        )}

        <MenuItem onClick={() => setOpen('expiry')}>
          <CalendarClock size={16} strokeWidth={1.75} aria-hidden />
          {expiresAt === null ? 'Poner fecha de caducidad' : 'Cambiar la caducidad'}
        </MenuItem>

        <MenuItem tone="danger" onClick={() => setOpen('delete')}>
          <Trash2 size={16} strokeWidth={1.75} aria-hidden />
          Borrar
        </MenuItem>
      </Menu>

      {failure !== null && (
        <Banner tone="danger" live="alert" className="mt-3">
          {failure}
        </Banner>
      )}

      {/* Montado y desmontado, para que el campo nazca con el nombre actual sin
          sincronizarlo en un efecto. */}
      {open === 'expiry' && (
        <ExpiryDialog
          projectId={projectId}
          expiresAt={expiresAt}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null)
            refresh()
          }}
        />
      )}

      {open === 'rename' && (
        <RenameDialog
          projectId={projectId}
          name={name}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null)
            refresh()
          }}
        />
      )}

      <Dialog
        open={open === 'archive'}
        onClose={() => setOpen(null)}
        title="¿Archivar este proyecto?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(null)}>
              Cancelar
            </Button>
            <Button
              busy={setStatus.isPending && 'Archivando…'}
              onClick={() => setStatus.mutate('archived')}
            >
              Archivar
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-muted">
          Se marca como terminado para que no te estorbe en la lista. Puedes sacarlo del archivo
          cuando quieras.
        </p>
        <p className="mt-3 text-body text-ink-muted">
          No cambia nada más: los enlaces que ya compartiste siguen funcionando y tu cliente puede
          seguir viendo y descargando sus fotos.
        </p>
      </Dialog>

      <Dialog
        open={open === 'delete'}
        onClose={() => setOpen(null)}
        title={`¿Borrar «${name}»?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              busy={remove.isPending && 'Borrando…'}
              onClick={() => remove.mutate()}
            >
              Borrar el proyecto
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-muted">
          <strong className="font-medium text-ink">
            Los enlaces que compartiste dejarán de funcionar.
          </strong>{' '}
          Si tu cliente abre el suyo, verá que la galería ya no existe. Avísale antes.
        </p>
        <p className="mt-3 text-body text-ink-muted">
          Liberas {formatBytes(storageBytes)} de tu plan al momento, y el proyecto se queda en la
          papelera <strong className="font-medium text-ink">7 días</strong> por si te arrepientes.
          Pasados esos días se borra de verdad y ya no hay vuelta atrás.
        </p>
        <p className="mt-3 text-body text-ink-muted">
          Si sólo quieres quitarlo de en medio, archívalo en vez de borrarlo.
        </p>
      </Dialog>
    </>
  )
}

function RenameDialog({
  projectId,
  name,
  onClose,
  onDone,
}: {
  projectId: string
  name: string
  onClose: () => void
  onDone: () => void
}) {
  const [value, setValue] = useState(name)
  const [failure, setFailure] = useState<string | null>(null)

  const rename = useMutation({
    mutationFn: () =>
      api.patch<{ data: unknown }>(`/projects/${projectId}`, { name: value.trim() }),
    onSuccess: onDone,
    onError: (error) =>
      setFailure(error instanceof ApiException ? error.message : 'No se pudo renombrar.'),
  })

  const ready = value.trim() !== '' && value.trim() !== name

  return (
    <Dialog
      open
      onClose={onClose}
      title="Renombrar el proyecto"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!ready}
            busy={rename.isPending && 'Guardando…'}
            onClick={() => {
              setFailure(null)
              rename.mutate()
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <Field
        label="Nombre"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        maxLength={200}
        autoFocus
        // La dirección del proyecto no cambia al renombrar: el slug se fija al
        // crearlo y los enlaces ya repartidos lo llevan dentro. Cambiarlo
        // rompería la galería de un cliente por un cambio de rótulo.
        hint="Sólo cambia el nombre que ves tú. La dirección del proyecto y los enlaces que ya compartiste no cambian."
      />

      {failure !== null && (
        <Banner tone="danger" live="alert" className="mt-4">
          {failure}
        </Banner>
      )}
    </Dialog>
  )
}

/**
 * La fecha en que la entrega deja de abrirse.
 *
 * `projects.expires_at` existía desde F0 y no la leía nadie: se podía guardar
 * una fecha y no pasaba nada. Ahora manda sobre todos los enlaces del proyecto
 * a la vez, que es lo que un estudio espera — poner fecha a una entrega, no ir
 * uno por uno por los cinco enlaces que repartió.
 *
 * Se monta y desmonta con el diálogo para que el campo nazca con lo guardado.
 */
function ExpiryDialog({
  projectId,
  expiresAt,
  onClose,
  onDone,
}: {
  projectId: string
  expiresAt: string | null
  onClose: () => void
  onDone: () => void
}) {
  // `<input type="date">` quiere AAAA-MM-DD, y hay que sacarlo en el huso del
  // estudio: cortar el ISO por el carácter 10 devuelve el día en UTC, que a
  // partir de las 19:00 en Lima ya es el siguiente.
  const [value, setValue] = useState(expiresAt === null ? '' : toStudioDate(expiresAt))
  const [failure, setFailure] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ data: unknown }>(`/projects/${projectId}`, {
        // Al final del día elegido y no al principio: quien escribe «31 de
        // marzo» quiere que ese día su cliente todavía pueda entrar. Y con el
        // huso puesto, o «todo ese día» se queda en cinco horas menos.
        expires_at: value === '' ? null : endOfDayInStudioZone(value),
      }),
    onSuccess: onDone,
    onError: (error) =>
      setFailure(error instanceof ApiException ? error.message : 'No se pudo guardar la fecha.'),
  })

  const hoy = toStudioDate(new Date().toISOString())

  return (
    <Dialog
      open
      onClose={onClose}
      title="¿Hasta cuándo puede entrar tu cliente?"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          {expiresAt !== null && (
            <Button
              variant="secondary"
              onClick={() => {
                setValue('')
                setFailure(null)
                save.mutate()
              }}
            >
              Quitar la fecha
            </Button>
          )}
          <Button
            busy={save.isPending && 'Guardando…'}
            disabled={value === ''}
            onClick={() => {
              setFailure(null)
              save.mutate()
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <p className="text-body text-ink-muted">
        A partir de esa fecha, los enlaces que compartiste dejan de abrirse y tu cliente ya no puede
        ver ni descargar sus fotos.
      </p>

      <p className="mt-3 text-body text-ink-muted">
        {/* Lo más importante del diálogo: caducar no es borrar. Sin esto,
            alguien pone fecha creyendo que así libera espacio. */}
        <strong className="font-medium text-ink">Los archivos no se borran</strong> ni dejan de
        ocupar tu plan. Si más adelante amplías la fecha, los mismos enlaces vuelven a funcionar sin
        mandar nada nuevo.
      </p>

      <p className="mt-3 text-body text-ink-muted">Te avisamos por correo 7 días antes, y otra vez la víspera.</p>

      <div className="mt-5">
        <Field
          label="Caduca el"
          type="date"
          value={value}
          min={hoy}
          onChange={(event) => setValue(event.target.value)}
          hint="Sigue abierta durante todo ese día."
        />
      </div>

      {failure !== null && (
        <Banner tone="danger" live="alert" className="mt-4">
          {failure}
        </Banner>
      )}
    </Dialog>
  )
}
